import React, { useState, useEffect } from 'react';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import { Breadcrumb, BreadcrumbItem } from 'reactstrap';
import { Link } from "react-router-dom";
import Container from '@material-ui/core/Container';
import Grid from "@material-ui/core/Grid";
import { Typography, TextField, FormControl, FormLabel, RadioGroup, FormControlLabel, Radio, Button, MenuItem } from '@mui/material';
import List from "@material-ui/core/List";
import { Helmet } from "react-helmet";
import CssBaseline from "@material-ui/core/CssBaseline";
import { makeStyles } from "@material-ui/core/styles";
import wallPaper from "../assets/static/images/kea-apply-wall-paper-4.png";
import keaLogo from '../assets/static/images/king-edward-zim-logo.png';
import credentials from "../config/credentials";
import { BRAND_NAME } from "../shared/consts";

const useStyles = makeStyles((theme) => ({
  heroContent: {
    padding: theme.spacing(14, 0, 6),
    backgroundImage: `url(${wallPaper})`,
    backgroundColor: 'rgba(0, 0, 0, 0.68)',
    backgroundBlendMode: 'darken',
    backgroundSize: 'cover'
  }
}));

export default function Apply(props) {
  const classes = useStyles();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const [submitted, setSubmitted] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [openSnackbar, setOpenSnackbar] = useState(false);


  const [form, setForm] = useState({
    childName: '',
    dob: '',
    gender: '',
    address: '',
    specialNeeds: '',
    entryDate: '',
    currentSchool: '',
    parentName: '',
    parentPhone: '',
    parentEmail: '',
    relationship: '',
    coupon: ''
  });

  const [discount, setDiscount] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));

    // Clear the error as soon as the parent starts correcting the field.
    setFieldErrors(prev => {
      if (!prev[name]) {
        return prev;
      }
      const { [name]: removed, ...rest } = prev;
      return rest;
    });

    if (name === 'coupon') {
      const code = value.trim().toLowerCase();
      if (['kea10', 'kb10', 'kc10'].includes(code)) {
        setDiscount(true);
      } else {
        setDiscount(false);
      }
    }
  };

  // We cannot follow an application up without a way to reach the parent, so
  // these details are required before the form can be submitted.
  const validate = () => {
    const errors = {};

    if (!form.childName.trim()) {
      errors.childName = "Please enter your child's full name";
    }
    if (!form.parentName.trim()) {
      errors.parentName = 'Please enter your full name';
    }

    const phone = form.parentPhone.trim();
    if (!phone) {
      errors.parentPhone = 'Please enter a telephone number so we can contact you';
    } else if (!/^\+?[\d\s()-]{7,}$/.test(phone)) {
      errors.parentPhone = 'Please enter a valid telephone number';
    }

    const email = form.parentEmail.trim();
    if (!email) {
      errors.parentEmail = 'Please enter an email address so we can send you feedback';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      errors.parentEmail = 'Please enter a valid email address';
    }

    if (!form.relationship.trim()) {
      errors.relationship = 'Please tell us your relationship to the child';
    }

    return errors;
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    const errors = validate();
    setFieldErrors(errors);

    if (Object.keys(errors).length > 0) {
      setSuccessMessage('');
      setErrorMessage('Please complete the required details so we can get back to you.');
      setOpenSnackbar(true);
      return;
    }

    setErrorMessage('');

    const payload = {
      ...form,
      discountApplied: discount,
      siteID: 'kea'
    };

    console.log("Form Submission Payload:", payload);
    const apiURL = `${credentials.API_URL_INQUIRY}/contact-form/`

    // Example POST request to your endpoint
    // Replace '/api/apply' with your actual endpoint URL
    fetch(apiURL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(payload)
    })
    .then(res => {
      if (res.ok) {
        setSuccessMessage("✅ Application submitted successfully!");
        setSubmitted(true);
        setOpenSnackbar(true);
        window.scrollTo({ top: 0, behavior: 'smooth' });
      } else {
        throw new Error('Submission failed');
      }
    })
    .catch(error => {
      console.error("Submission error:", error);
      setErrorMessage("❌ " + error.message);
      setOpenSnackbar(true);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    });
  };


  return (
    <React.Fragment>
      <Helmet>
        <meta name="description" content="Apply for a place at King Edwards Academy" />
        <title>{BRAND_NAME} - Apply</title>
      </Helmet>
      <CssBaseline />
      <Snackbar
        open={openSnackbar}
        autoHideDuration={6000}
        onClose={() => setOpenSnackbar(false)}
      >
        <Alert
          onClose={() => setOpenSnackbar(false)}
          severity={errorMessage ? "error" : "success"}
          sx={{ width: '100%' }}
        >
          {errorMessage || successMessage}
        </Alert>
      </Snackbar>

      <main>
        <div className={classes.heroContent}>
          <Breadcrumb>
            <BreadcrumbItem><Link to="/">Home</Link></BreadcrumbItem>
            <BreadcrumbItem active>Apply</BreadcrumbItem>
          </Breadcrumb>
          <Container maxWidth="sm">
            <Typography component="h1" variant="h4" align="center" color="textPrimary" style={{ fontWeight: 'bold' }} gutterBottom>
              <span style={{ backgroundColor: 'rgb(245, 195, 0)', padding: '0.0em 0.2em 0.0em 0.2em' }}>Apply</span>
            </Typography>
          </Container>
        </div>
      </main>
      <div className='container'>
        <Container maxWidth="md">
          <div style={{ paddingTop: '30px', paddingBottom: '50px' }}>
            <List>
              <Grid container spacing={3}>
                <Grid item xs={12} sm={6}>
                  <Typography gutterBottom>
                    We are delighted that you are considering King Edwards Academy for your child.
                    <br /><br />
                    Applying for a place is straightforward, please complete our online application form.
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <div style={{ textAlign: 'center', backgroundColor: '#f5f9fc', padding: '20px 4px' }}>
                    <div style={{ paddingBottom: '20px' }}>
                      <img style={{ maxHeight: '75px' }} src={keaLogo} alt="KEA Logo" />
                    </div>

                    {submitted ? (
                      <Typography variant="h6" style={{ color: 'green' }}>
                        ✅ Thank you! Your application has been submitted successfully. We will be in touch shortly.
                      </Typography>
                    ) : (
                      <form onSubmit={handleSubmit} noValidate>
                        {/* Child Details */}
                        <Typography variant="h6" gutterBottom>Child Details</Typography>
                        <TextField fullWidth required label="Full Name" name="childName" value={form.childName} onChange={handleChange} margin="normal"
                                   error={Boolean(fieldErrors.childName)} helperText={fieldErrors.childName} />
                        <TextField fullWidth type="date" name="dob" value={form.dob} onChange={handleChange} margin="normal" label="Date of Birth" InputLabelProps={{ shrink: true }} />
                        <FormControl fullWidth margin="normal">
                          <FormLabel>Gender</FormLabel>
                          <RadioGroup row name="gender" value={form.gender} onChange={handleChange}>
                            <FormControlLabel value="Male" control={<Radio />} label="Male" />
                            <FormControlLabel value="Female" control={<Radio />} label="Female" />
                            <FormControlLabel value="Other" control={<Radio />} label="Other" />
                          </RadioGroup>
                        </FormControl>
                        <TextField fullWidth label="Address" name="address" value={form.address} onChange={handleChange} margin="normal" />
                        <FormControl fullWidth margin="normal">
                          <FormLabel>Does your child have any special educational needs or a disability?</FormLabel>
                          <RadioGroup row name="specialNeeds" value={form.specialNeeds} onChange={handleChange}>
                            <FormControlLabel value="Yes" control={<Radio />} label="Yes" />
                            <FormControlLabel value="No" control={<Radio />} label="No" />
                          </RadioGroup>
                        </FormControl>
                        <TextField fullWidth type="month" name="entryDate" value={form.entryDate} onChange={handleChange} margin="normal" label="Proposed Year of Entry" InputLabelProps={{ shrink: true }} />
                        <TextField fullWidth label="Current School / Nursery" name="currentSchool" value={form.currentSchool} onChange={handleChange} margin="normal" />
                        <br/>
                        {/* Parent/Guardian */}
                        <Typography variant="h6" gutterBottom style={{ marginTop: '48px' }}>Parent/Guardian Details</Typography>
                        <Typography variant="body2" align="left" style={{ color: '#686868', marginBottom: '8px' }}>
                          We need these details so that we can get back to you about your application.
                        </Typography>
                        <TextField fullWidth required label="Name" name="parentName" value={form.parentName} onChange={handleChange} margin="normal"
                                   error={Boolean(fieldErrors.parentName)} helperText={fieldErrors.parentName} />
                        <TextField fullWidth required label="Telephone Number" name="parentPhone" value={form.parentPhone} onChange={handleChange} margin="normal"
                                   error={Boolean(fieldErrors.parentPhone)} helperText={fieldErrors.parentPhone} />
                        <TextField fullWidth required type="email" label="Email" name="parentEmail" value={form.parentEmail} onChange={handleChange} margin="normal"
                                   error={Boolean(fieldErrors.parentEmail)} helperText={fieldErrors.parentEmail} />
                        <TextField fullWidth required label="Relationship to Child" name="relationship" value={form.relationship} onChange={handleChange} margin="normal"
                                   error={Boolean(fieldErrors.relationship)} helperText={fieldErrors.relationship} />

                        {/* Discount */}
                        <TextField fullWidth label="Discount Code" name="coupon" value={form.coupon} onChange={handleChange} margin="normal" />
                        {discount && (
                          <Typography variant="body2" style={{ color: 'green', marginTop: '5px' }}>
                            🎉 10% discount will be applied!
                          </Typography>
                        )}

                        {/* Submit Button */}
                        <Button disableElevation
                                variant="contained"
                                color="primary"
                                type="submit" style={{ marginTop: '32px', marginBottom: '32px' }}>
                          Submit Application
                        </Button>
                      </form>
                    )}


                  </div>
                </Grid>
              </Grid>
            </List>
          </div>
        </Container>
      </div>
    </React.Fragment>
  );
}
